/**
 * RevealButton — Stage 3 Opt-In Reveal (FE-016 / DES-007)
 *
 * Appears at 90s when isRevealEligible becomes true.
 * Warm, inviting CTA with dual opt-in guarantee.
 *
 * States:
 *   'available'  — "Ready to reveal?" with subtext "Both people must agree"
 *   'requested'  — Brief local state before server confirms
 *   'waiting'    — "Waiting for partner..." with gentle pulse
 *   'accepted'   — Brief celebration, then transitions to Stage 3
 *
 * IMPORTANT: No visible signal is sent to the peer until BOTH have opted in.
 * Touch-friendly: minimum 48px tap target (exceeds 44px requirement).
 */

import { type Component, Show } from "solid-js";
import type { StageManager } from "../services/stage-manager";
import styles from "./RevealButton.module.css";

export interface RevealButtonProps {
  stageManager: StageManager;
}

/** Inline SVG: Sparkle/reveal icon */
function RevealIcon() {
  return (
    <svg
      class={styles.icon}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M10 2L11.5 7.5L17 6L12.5 10L17 14L11.5 12.5L10 18L8.5 12.5L3 14L7.5 10L3 6L8.5 7.5L10 2Z"
        fill="currentColor"
        stroke="currentColor"
        stroke-width="0.5"
        stroke-linejoin="round"
      />
    </svg>
  );
}

/** Inline SVG: Checkmark icon for accepted state */
function CheckIcon() {
  return (
    <svg
      class={styles.icon}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4 10L8 14L16 6"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

const RevealButton: Component<RevealButtonProps> = (props) => {
  const isEligible = () => props.stageManager.isRevealEligible();
  const revealState = () => props.stageManager.revealState();

  const isAvailable = () =>
    revealState() === "none" && isEligible();
  const isRequested = () =>
    revealState() === "requested";
  const isWaiting = () =>
    revealState() === "waiting";
  const isAccepted = () =>
    revealState() === "accepted";

  const handleClick = () => {
    if (isAvailable()) {
      props.stageManager.requestReveal();
    }
  };

  const buttonClass = () => {
    if (isWaiting() || isRequested()) return `${styles.button} ${styles.buttonWaiting}`;
    if (isAccepted()) return `${styles.button} ${styles.buttonAccepted}`;
    return styles.button;
  };

  const buttonLabel = () => {
    if (isWaiting() || isRequested()) return "Waiting for partner...";
    if (isAccepted()) return "Revealed!";
    return "Ready to reveal?";
  };

  return (
    <Show when={isEligible()}>
      <div class={styles.container}>
        <button
          class={buttonClass()}
          onClick={handleClick}
          disabled={isWaiting() || isRequested() || isAccepted()}
          type="button"
          aria-label={buttonLabel()}
        >
          <Show when={isAccepted()} fallback={<RevealIcon />}>
            <CheckIcon />
          </Show>
          <span>{buttonLabel()}</span>
        </button>

        <Show when={isAvailable()}>
          <p class={styles.subtext}>Both people must agree</p>
        </Show>

        <Show when={isWaiting() || isRequested()}>
          <p class={styles.subtext}>Your choice is private until they agree too</p>
        </Show>
      </div>
    </Show>
  );
};

export default RevealButton;
